import type { CashbookExportPermission } from '@finvox/shared/cashbook-access';
import {
  cashbookCustomNote,
  type CashbookFundingSourcePostable,
  type CashbookSheetFooter,
  type CashbookSheetRow,
  type CashbookViewMode
} from '@finvox/shared/cashbook';
import { toMoney } from '@finvox/shared/money';
import { format, parseISO } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { IconButton } from '@/components/ui/IconButton';
import { MoneyDisplay } from '@/components/ui/MoneyDisplay';
import { useToast } from '@/contexts/ToastContext';
import {
  downloadCashbookCsv,
  downloadCashbookPdf,
  downloadCashbookXlsx,
  type CashbookExportInput
} from '@/lib/cashbookExport';
import { emailCashbookExportToOwner } from '@/lib/cashbookExportEmail';
import { cn } from '@/lib/cn';
import type { TFunction } from 'i18next';
import { displayCashbookEntryLabel } from '@/lib/cashbookEntryLabel';
import type { CashbookPeriodDrillTarget } from '@/lib/cashbookPeriodUrl';

type Props = {
  rows: CashbookSheetRow[];
  footer: CashbookSheetFooter | null;
  loading: boolean;
  initialLoading?: boolean;
  viewMode?: CashbookViewMode;
  canVoid: boolean;
  periodOpen: boolean;
  exportInput: CashbookExportInput | null;
  showBalanceColumn?: boolean;
  exportAccess?: Partial<Record<CashbookExportPermission, boolean>>;
  orgId?: string | null;
  online?: boolean;
  highlight?: boolean;
  canDrillToDay?: boolean;
  canDrillToMonth?: boolean;
  onPeriodDrillDown?: (target: CashbookPeriodDrillTarget) => void;
  onVoid?: (entryId: string) => void;
  canClassify?: boolean;
  onClassify?: (entryId: string, fundingSource: CashbookFundingSourcePostable) => void;
};

function rowDescription(
  row: CashbookSheetRow,
  viewMode: CashbookViewMode,
  locale: typeof enUS | typeof ar,
  openingRowLabel: string,
  t: TFunction,
  isArabic: boolean,
  compact = false
): string {
  if (row.source === 'opening') return openingRowLabel;
  if (viewMode === 'month') {
    return format(parseISO(row.date), compact ? 'PP' : 'EEEE, PPP', { locale });
  }
  if (viewMode === 'multi' || viewMode === 'year') {
    return format(parseISO(row.date), 'MMMM yyyy', { locale });
  }
  const key = row.categoryKey || row.label;
  return displayCashbookEntryLabel(key, t, isArabic);
}

function drillTargetForRow(
  row: CashbookSheetRow,
  viewMode: CashbookViewMode
): CashbookPeriodDrillTarget | null {
  if (row.source === 'opening') return null;
  if (viewMode === 'month') return { kind: 'day', date: row.date };
  if (viewMode === 'year' || viewMode === 'multi') {
    return { kind: 'month', month: row.date.slice(0, 7) };
  }
  return null;
}

export function CashbookSheet({
  rows,
  footer,
  loading,
  initialLoading = false,
  viewMode = 'day',
  canVoid,
  periodOpen,
  exportInput,
  showBalanceColumn = true,
  exportAccess,
  orgId,
  online = true,
  highlight = false,
  canDrillToDay = true,
  canDrillToMonth = true,
  onPeriodDrillDown,
  onVoid,
  canClassify = false,
  onClassify
}: Props) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const [classifyForId, setClassifyForId] = useState<string | null>(null);
  const locale = i18n.language.startsWith('ar') ? ar : enUS;
  const isArabic = i18n.language.startsWith('ar');
  const canExportCsv = Boolean(exportInput && rows.length > 0 && (exportAccess?.csv ?? true));
  const canExportXlsx = Boolean(exportInput && rows.length > 0 && (exportAccess?.xlsx ?? true));
  const canExportPdf = Boolean(exportInput && rows.length > 0 && (exportAccess?.pdf ?? true));
  const showExportBar = canExportCsv || canExportXlsx || canExportPdf;
  const showNotesColumn = viewMode === 'day';
  const colCount = (showBalanceColumn ? 6 : 5) + (showNotesColumn ? 1 : 0);
  const showOverlay = loading && rows.length > 0;
  const showEmptyLoading = initialLoading && rows.length === 0;
  const showFooter =
    footer != null &&
    (rows.length > 0 ||
      toMoney(footer.endBalance).gt(0) ||
      toMoney(footer.totalIn).gt(0) ||
      toMoney(footer.totalOut).gt(0));

  function canDrill(target: CashbookPeriodDrillTarget | null): boolean {
    if (!target || !onPeriodDrillDown) return false;
    if (target.kind === 'day') return canDrillToDay;
    return canDrillToMonth;
  }

  function renderDescription(row: CashbookSheetRow, compact = false) {
    const text = rowDescription(
      row,
      viewMode,
      locale,
      t('cashbook.openingRow'),
      t,
      isArabic,
      compact
    );
    const target = drillTargetForRow(row, viewMode);
    if (!canDrill(target) || !target) return text;
    const aria =
      target.kind === 'day'
        ? t('cashbook.drillToDay', { date: target.date })
        : t('cashbook.drillToMonth', { month: target.month });
    return (
      <button
        type="button"
        className="text-start font-medium text-[var(--fv-primary)] underline-offset-2 hover:underline focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--fv-primary)]"
        aria-label={aria}
        onClick={() => onPeriodDrillDown?.(target)}
      >
        {text}
      </button>
    );
  }

  async function notifyOwner(
    format: 'csv' | 'xlsx' | 'pdf',
    input: CashbookExportInput
  ): Promise<void> {
    if (!orgId || !online) return;
    try {
      const result = await emailCashbookExportToOwner({ orgId, format, input, viewMode });
      if (result.sent) {
        toast.success(t('cashbook.exportEmailSent'), 'cashbook-export-email');
      } else {
        toast.error(t('cashbook.exportEmailFailed'), 'cashbook-export-email');
      }
    } catch {
      toast.error(t('cashbook.exportEmailFailed'), 'cashbook-export-email');
    }
  }

  function handleExportCsv(): void {
    if (!exportInput) return;
    downloadCashbookCsv(exportInput);
    void notifyOwner('csv', exportInput);
  }

  function handleExportXlsx(): void {
    if (!exportInput) return;
    downloadCashbookXlsx(exportInput);
    void notifyOwner('xlsx', exportInput);
  }

  async function handleExportPdf(): Promise<void> {
    if (!exportInput) return;
    await downloadCashbookPdf(exportInput);
    void notifyOwner('pdf', exportInput);
  }

  return (
    <div className="mt-6">
      {showExportBar ? (
        <div className="mb-3">
          <div className="flex flex-row flex-wrap justify-end gap-2">
          {canExportCsv ? (
            <Button
              variant="secondary"
              size="sm"
              className="min-w-0 flex-1 sm:flex-none sm:w-auto"
              disabled={!canExportCsv}
              onClick={handleExportCsv}
            >
              {t('cashbook.exportCsv')}
            </Button>
          ) : null}
          {canExportXlsx ? (
            <Button
              variant="secondary"
              size="sm"
              className="min-w-0 flex-1 sm:flex-none sm:w-auto"
              disabled={!canExportXlsx}
              onClick={handleExportXlsx}
            >
              {t('cashbook.exportExcel')}
            </Button>
          ) : null}
          {canExportPdf ? (
            <Button
              variant="secondary"
              size="sm"
              className="min-w-0 flex-1 sm:flex-none sm:w-auto"
              disabled={!canExportPdf}
              onClick={() => void handleExportPdf()}
            >
              {t('cashbook.exportPdf')}
            </Button>
          ) : null}
          </div>
        </div>
      ) : null}
      <div
        className={cn(
          'relative rounded-lg border border-[var(--fv-border)] transition-[box-shadow] duration-500',
          highlight && 'ring-2 ring-[var(--fv-primary)] ring-offset-2 ring-offset-[var(--fv-bg)]'
        )}
      >
        {showOverlay ? (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center bg-[var(--fv-surface)]/70"
            aria-live="polite"
            aria-busy="true"
          >
            <div className="flex items-center gap-2 text-sm text-[var(--fv-text-muted)]">
              <span
                className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-[var(--fv-border)] border-t-[var(--fv-primary)]"
                aria-hidden
              />
              {t('common.loading')}
            </div>
          </div>
        ) : null}

        <div className="md:hidden">
          {showEmptyLoading ? (
            <div className="flex flex-col items-center gap-2 p-12 text-center text-[var(--fv-text-muted)]">
              <span
                className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-[var(--fv-border)] border-t-[var(--fv-primary)]"
                aria-hidden
              />
              {t('common.loading')}
            </div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-[var(--fv-text-muted)]">{t('common.noResults')}</div>
          ) : (
            <ul className="divide-y divide-[var(--fv-border)]">
              {rows.map((row, i) => {
                const rawNote = showNotesColumn
                  ? cashbookCustomNote(row.memo, row.categoryKey)
                  : undefined;
                const note = rawNote
                  ? displayCashbookEntryLabel(rawNote, t, isArabic)
                  : undefined;
                const showVoid =
                  canVoid &&
                  periodOpen &&
                  row.cashbookEntryId &&
                  row.source === 'cashbook' &&
                  !row.voided;

                return (
                  <li
                    key={row.id}
                    className={cn(
                      'p-3',
                      i % 2 === 1 && 'bg-[var(--fv-bg)]/50',
                      row.voided && 'opacity-50 line-through decoration-[var(--fv-text-muted)]'
                    )}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-xs tabular-nums text-[var(--fv-text-muted)]">{row.date}</p>
                      {showVoid ? (
                        <IconButton
                          aria-label={t('cashbook.void')}
                          variant="ghost"
                          size="sm"
                          onClick={() => onVoid?.(row.cashbookEntryId!)}
                        >
                          ×
                        </IconButton>
                      ) : null}
                    </div>
                    <div className="mt-1 break-words font-medium text-[var(--fv-text)]">
                      {renderDescription(row, true)}
                    </div>
                    {note ? (
                      <p className="mt-1 break-words text-sm text-[var(--fv-text-muted)]">
                        <span className="font-medium text-[var(--fv-text)]">{t('cashbook.colNotes')}: </span>
                        {note}
                      </p>
                    ) : null}
                    {row.outAmount !== '0.00' && row.fundingSource ? (
                      <p className="mt-1 text-xs text-[var(--fv-text-muted)]">
                        {row.fundingSource === 'drawer'
                          ? t('cashbook.funding.fromDrawer')
                          : row.fundingSource === 'treasury'
                            ? t('cashbook.funding.fromTreasury')
                            : t('cashbook.funding.needsType')}
                      </p>
                    ) : null}
                    {row.drawerCount != null && row.salesOrigin === 'drawer' ? (
                      <p className="mt-1 text-xs text-[var(--fv-text-muted)]">
                        {t('cashbook.drawerCount.sheetHint', { amount: row.drawerCount })}
                      </p>
                    ) : null}
                    {canClassify &&
                    viewMode === 'day' &&
                    row.cashbookEntryId &&
                    !row.voided &&
                    row.outAmount !== '0.00' &&
                    (row.fundingSource === 'unclassified' || !row.fundingSource) ? (
                      <div className="mt-2">
                        {classifyForId === row.cashbookEntryId ? (
                          <div className="flex flex-col gap-2">
                            <Button
                              type="button"
                              variant="secondary"
                              className="min-h-11 w-full"
                              onClick={() => {
                                onClassify?.(row.cashbookEntryId!, 'drawer');
                                setClassifyForId(null);
                              }}
                            >
                              {t('cashbook.funding.fromDrawer')}
                            </Button>
                            <Button
                              type="button"
                              variant="secondary"
                              className="min-h-11 w-full"
                              onClick={() => {
                                onClassify?.(row.cashbookEntryId!, 'treasury');
                                setClassifyForId(null);
                              }}
                            >
                              {t('cashbook.funding.fromTreasury')}
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              className="min-h-11 w-full"
                              onClick={() => setClassifyForId(null)}
                            >
                              {t('common.cancel')}
                            </Button>
                          </div>
                        ) : (
                          <Button
                            type="button"
                            variant="secondary"
                            className="min-h-11 w-full"
                            onClick={() => setClassifyForId(row.cashbookEntryId!)}
                          >
                            {t('cashbook.funding.setType')}
                          </Button>
                        )}
                      </div>
                    ) : null}
                    <dl
                      className={cn(
                        'mt-2 grid gap-x-3 gap-y-2 text-sm',
                        showBalanceColumn ? 'grid-cols-3' : 'grid-cols-2'
                      )}
                    >
                      <div>
                        <dt className="text-xs text-[var(--fv-text-muted)]">{t('cashbook.colIn')}</dt>
                        <dd className="mt-0.5 text-end tabular-nums">
                          {row.inAmount !== '0.00' ? <MoneyDisplay value={row.inAmount} /> : '—'}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-[var(--fv-text-muted)]">{t('cashbook.colOut')}</dt>
                        <dd className="mt-0.5 text-end tabular-nums">
                          {row.outAmount !== '0.00' ? <MoneyDisplay value={row.outAmount} /> : '—'}
                        </dd>
                      </div>
                      {showBalanceColumn ? (
                        <div>
                          <dt className="text-xs text-[var(--fv-text-muted)]">{t('cashbook.colBalance')}</dt>
                          <dd className="mt-0.5 text-end font-medium tabular-nums">
                            <MoneyDisplay value={row.balance} />
                          </dd>
                        </div>
                      ) : null}
                    </dl>
                  </li>
                );
              })}
            </ul>
          )}
          {showFooter ? (
            <div className="border-t-2 border-[var(--fv-border)] bg-[var(--fv-surface)] p-3 font-semibold">
              <p className="text-sm">{t('cashbook.footerPeriodTotals')}</p>
              <dl
                className={cn(
                  'mt-2 grid gap-x-3 gap-y-2 text-sm',
                  showBalanceColumn ? 'grid-cols-3' : 'grid-cols-2'
                )}
              >
                <div>
                  <dt className="text-xs font-normal text-[var(--fv-text-muted)]">{t('cashbook.colIn')}</dt>
                  <dd className="mt-0.5 text-end tabular-nums">
                    <MoneyDisplay value={footer.totalIn} />
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-normal text-[var(--fv-text-muted)]">{t('cashbook.colOut')}</dt>
                  <dd className="mt-0.5 text-end tabular-nums">
                    <MoneyDisplay value={footer.totalOut} />
                  </dd>
                </div>
                {showBalanceColumn ? (
                  <div>
                    <dt className="text-xs font-normal text-[var(--fv-text-muted)]">
                      {t('cashbook.footerEndBalance')}
                    </dt>
                    <dd className="mt-0.5 text-end tabular-nums">
                      <MoneyDisplay value={footer.endBalance} />
                    </dd>
                  </div>
                ) : null}
              </dl>
            </div>
          ) : null}
        </div>

        <div className="hidden overflow-x-auto md:block">
        <table className="w-full min-w-[640px] table-fixed text-sm">
          <colgroup>
            <col className="w-[7.5rem]" />
            <col />
            {showNotesColumn ? <col /> : null}
            <col className="w-[6.5rem]" />
            <col className="w-[6.5rem]" />
            {showBalanceColumn ? <col className="w-[6.5rem]" /> : null}
            <col className="w-10" />
          </colgroup>
          <thead className="sticky top-0 bg-[var(--fv-surface)]">
            <tr className="border-b-2 border-[var(--fv-border)] text-start text-[var(--fv-text)]">
              <th className="p-2.5 text-start text-xs font-semibold uppercase tracking-wide">
                {t('cashbook.colDate')}
              </th>
              <th className="p-2.5 text-start text-xs font-semibold uppercase tracking-wide">
                {t('cashbook.colDescription')}
              </th>
              {showNotesColumn ? (
                <th className="p-2.5 text-start text-xs font-semibold uppercase tracking-wide">
                  {t('cashbook.colNotes')}
                </th>
              ) : null}
              <th className="p-2.5 text-end text-xs font-semibold uppercase tracking-wide tabular-nums">
                {t('cashbook.colIn')}
              </th>
              <th className="p-2.5 text-end text-xs font-semibold uppercase tracking-wide tabular-nums">
                {t('cashbook.colOut')}
              </th>
              {showBalanceColumn ? (
                <th className="p-2.5 text-end text-xs font-semibold uppercase tracking-wide tabular-nums">
                  {t('cashbook.colBalance')}
                </th>
              ) : null}
              <th className="p-2.5" aria-hidden />
            </tr>
          </thead>
          <tbody>
            {showEmptyLoading ? (
              <tr>
                <td colSpan={colCount} className="p-12 text-center text-[var(--fv-text-muted)]">
                  <div className="flex flex-col items-center gap-2">
                    <span
                      className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-[var(--fv-border)] border-t-[var(--fv-primary)]"
                      aria-hidden
                    />
                    {t('common.loading')}
                  </div>
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="p-8 text-center text-[var(--fv-text-muted)]">
                  {t('common.noResults')}
                </td>
              </tr>
            ) : (
              rows.map((row, i) => {
                const rawNote = showNotesColumn
                  ? cashbookCustomNote(row.memo, row.categoryKey)
                  : undefined;
                const note = rawNote
                  ? displayCashbookEntryLabel(rawNote, t, isArabic)
                  : undefined;
                return (
                <tr
                  key={row.id}
                  className={cn(
                    'border-t border-[var(--fv-border)]',
                    i % 2 === 1 && 'bg-[var(--fv-bg)]/50',
                    row.voided && 'opacity-50 line-through decoration-[var(--fv-text-muted)]'
                  )}
                >
                  <td className="p-2.5 whitespace-nowrap align-top tabular-nums">{row.date}</td>
                  <td className="p-2.5 align-top break-words">
                    <div>{renderDescription(row)}</div>
                    {row.outAmount !== '0.00' && row.fundingSource ? (
                      <p className="mt-1 text-xs text-[var(--fv-text-muted)]">
                        {row.fundingSource === 'drawer'
                          ? t('cashbook.funding.fromDrawer')
                          : row.fundingSource === 'treasury'
                            ? t('cashbook.funding.fromTreasury')
                            : t('cashbook.funding.needsType')}
                      </p>
                    ) : null}
                    {canClassify &&
                    viewMode === 'day' &&
                    row.cashbookEntryId &&
                    !row.voided &&
                    row.outAmount !== '0.00' &&
                    (row.fundingSource === 'unclassified' || !row.fundingSource) ? (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {classifyForId === row.cashbookEntryId ? (
                          <>
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              className="min-h-11"
                              onClick={() => {
                                onClassify?.(row.cashbookEntryId!, 'drawer');
                                setClassifyForId(null);
                              }}
                            >
                              {t('cashbook.funding.fromDrawer')}
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              className="min-h-11"
                              onClick={() => {
                                onClassify?.(row.cashbookEntryId!, 'treasury');
                                setClassifyForId(null);
                              }}
                            >
                              {t('cashbook.funding.fromTreasury')}
                            </Button>
                          </>
                        ) : (
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            className="min-h-11"
                            onClick={() => setClassifyForId(row.cashbookEntryId!)}
                          >
                            {t('cashbook.funding.setType')}
                          </Button>
                        )}
                      </div>
                    ) : null}
                  </td>
                  {showNotesColumn ? (
                    <td className="p-2.5 align-top break-words text-[var(--fv-text-muted)]">
                      {note ?? '—'}
                    </td>
                  ) : null}
                  <td className="p-2.5 text-end align-top tabular-nums">
                    {row.inAmount !== '0.00' ? <MoneyDisplay value={row.inAmount} /> : '—'}
                  </td>
                  <td className="p-2.5 text-end align-top tabular-nums">
                    {row.outAmount !== '0.00' ? <MoneyDisplay value={row.outAmount} /> : '—'}
                  </td>
                  {showBalanceColumn ? (
                    <td className="p-2.5 text-end align-top font-medium tabular-nums">
                      <MoneyDisplay value={row.balance} />
                    </td>
                  ) : null}
                  <td className="p-2.5 align-top">
                    {canVoid &&
                    periodOpen &&
                    row.cashbookEntryId &&
                    row.source === 'cashbook' &&
                    !row.voided ? (
                      <IconButton
                        aria-label={t('cashbook.void')}
                        variant="ghost"
                        size="sm"
                        onClick={() => onVoid?.(row.cashbookEntryId!)}
                      >
                        ×
                      </IconButton>
                    ) : null}
                  </td>
                </tr>
              );
              })
            )}
          </tbody>
          {showFooter ? (
            <tfoot className="border-t-2 border-[var(--fv-border)] bg-[var(--fv-surface)] font-semibold">
              <tr>
                <td className="p-2.5" colSpan={showNotesColumn ? 3 : 2}>
                  {t('cashbook.footerPeriodTotals')}
                </td>
                <td className="p-2.5 text-end tabular-nums">
                  <MoneyDisplay value={footer.totalIn} />
                </td>
                <td className="p-2.5 text-end tabular-nums">
                  <MoneyDisplay value={footer.totalOut} />
                </td>
                {showBalanceColumn ? (
                  <td className="p-2.5 text-end tabular-nums" title={t('cashbook.footerEndBalance')}>
                    <span className="sr-only">{t('cashbook.footerEndBalance')}: </span>
                    <MoneyDisplay value={footer.endBalance} />
                  </td>
                ) : null}
                <td />
              </tr>
            </tfoot>
          ) : null}
        </table>
        </div>
      </div>
    </div>
  );
}
